import { useEffect, useState } from 'react';
import {
  QuyenHoaDonDienTu,
  QuyenHoaDonDienTuInput,
  QuyenHoaDonDienTuResponse
} from '@/types/schemas/quyen-hoa-don-dien-tu.type';
import { useAuth } from '@/contexts/auth-context';
import QUERY_KEYS from '@/constants/query-keys';
import api from '@/lib/api';

interface UseQuyenHoaDonDienTuReturn {
  quyenHoaDonDienTus: QuyenHoaDonDienTu[];
  isLoading: boolean;
  addQuyenHoaDonDienTu: (newQuyenHoaDonDienTu: QuyenHoaDonDienTuInput) => Promise<QuyenHoaDonDienTu>;
  updateQuyenHoaDonDienTu: (
    uuid: string,
    updatedQuyenHoaDonDienTu: QuyenHoaDonDienTuInput
  ) => Promise<QuyenHoaDonDienTu>;
  deleteQuyenHoaDonDienTu: (uuid: string) => Promise<void>;
  refreshQuyenHoaDonDienTus: () => Promise<void>;
}

/**
 * Hook for managing QuyenHoaDonDienTu (Electronic Invoice Book) data
 *
 * This hook provides functions to fetch, create, update, and delete electronic invoice books.
 */
export const useQuyenHoaDonDienTu = (
  initialQuyenHoaDonDienTus: QuyenHoaDonDienTu[] = []
): UseQuyenHoaDonDienTuReturn => {
  const [quyenHoaDonDienTus, setQuyenHoaDonDienTus] = useState<QuyenHoaDonDienTu[]>(initialQuyenHoaDonDienTus);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchQuyenHoaDonDienTus = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<QuyenHoaDonDienTuResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.QUYEN_HOA_DON_DIEN_TU}/`
      );
      setQuyenHoaDonDienTus(response.data.results);
    } catch (error) {
      console.error('Error fetching electronic invoice books:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addQuyenHoaDonDienTu = async (newQuyenHoaDonDienTu: QuyenHoaDonDienTuInput): Promise<QuyenHoaDonDienTu> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.post<QuyenHoaDonDienTu>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.QUYEN_HOA_DON_DIEN_TU}/`,
        newQuyenHoaDonDienTu
      );
      const addedQuyenHoaDonDienTu = response.data;

      setQuyenHoaDonDienTus(prev => [...prev, addedQuyenHoaDonDienTu]);
      return addedQuyenHoaDonDienTu;
    } catch (error) {
      console.error('Error adding electronic invoice book:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuyenHoaDonDienTu = async (
    uuid: string,
    updatedQuyenHoaDonDienTu: QuyenHoaDonDienTuInput
  ): Promise<QuyenHoaDonDienTu> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.put<QuyenHoaDonDienTu>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.QUYEN_HOA_DON_DIEN_TU}/${uuid}/`,
        updatedQuyenHoaDonDienTu
      );
      const updatedQuyenHoaDonDienTuData = response.data;

      setQuyenHoaDonDienTus(prev =>
        prev.map(item => (item.uuid === updatedQuyenHoaDonDienTuData.uuid ? updatedQuyenHoaDonDienTuData : item))
      );

      return updatedQuyenHoaDonDienTuData;
    } catch (error) {
      console.error('Error updating electronic invoice book:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteQuyenHoaDonDienTu = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.QUYEN_HOA_DON_DIEN_TU}/${uuid}/`);
      setQuyenHoaDonDienTus(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting electronic invoice book:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshQuyenHoaDonDienTus = async (): Promise<void> => {
    await fetchQuyenHoaDonDienTus();
  };

  useEffect(() => {
    fetchQuyenHoaDonDienTus();
  }, [entity?.slug]);

  return {
    quyenHoaDonDienTus,
    isLoading,
    addQuyenHoaDonDienTu,
    updateQuyenHoaDonDienTu,
    deleteQuyenHoaDonDienTu,
    refreshQuyenHoaDonDienTus
  };
};
